import React, { useCallback, useMemo } from 'react';
import { isNull, isUndefined } from 'lodash/fp';
import Lottie from 'lottie-react';

import { getCurrentSelectedQueries, MLTestDocument, scheduleStartAndEndPageUpdateValue, setSchedulePage, addNewPlaygroundDocumentQuery, getConfirmationModalVisible, toggleConfirmationModal, getSelectedQueriesUpdated, askAIPlaygroundQuestionsStarted, getIsAskingAIPlaygroundQuestions, getAIResults, getPlaygroundQuestionsConfirmDisabled, resetSelectedQueries, resetAIResults, deleteNewQuestionPlaygroundQueries, AIPlaygroundTab, getSelectedAIPlaygroundTab, setSelectedAIPlaygroundTab, getProposedAIPlaygroundTab, updateProposedTab } from './store';
import styles from './AIPlayground.module.scss';
import { Text } from '../../../shared/text/Text';
import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { AIPlaygroundQuerySelect } from './AIPlaygroundQuerySelect';
import { ArrowRight, WarningSign } from '../../../shared/icons';
import { IconButton } from '../../../shared/button/IconButton';
import { AIPlaygroundQuery } from './AIPlaygroundQuery';
import { ConfirmationModal } from '../../../shared/modal/ConfirmationModal';
import { ModalHeader } from '../../../shared/modal/ModalHeader';
import { OverflowTooltip } from '../../../shared/tooltip';
import { AIPlaygroundPreviouslyAskedQuestions } from './AIPlaygroundPreviouslyAskedQuestions';
import { Wizard } from '../../../shared/wizard/Wizard';
import { LaboratoryScience } from '../../../shared/icons/LaboratoryScience';
import { getNumberOfPages } from '../../../documents/document-viewer/store';
import { getLottieAnimation, LottieAntimationTypes } from '../../../../assets/lottie/getLottieAnimation';

const { amber, primary } = styles;

export interface MLTestDocumentPlaygroundProps {
    mlTestDocument: MLTestDocument;
}

export const MLTestDocumentPlayground: React.FC<MLTestDocumentPlaygroundProps> = ({ mlTestDocument }) => {
    const dispatch = useAppDispatch();

    const { scheduleStartPage, scheduleEndPage } = mlTestDocument;

    const selectedQueries = useAppSelector(getCurrentSelectedQueries);
    const confirmationModalVisible = useAppSelector(getConfirmationModalVisible);
    const queriesEqual = useAppSelector(getSelectedQueriesUpdated);
    const isAskingAIQuestions = useAppSelector(getIsAskingAIPlaygroundQuestions);
    const documentNumPages = useAppSelector(getNumberOfPages);
    const aiResults = useAppSelector(getAIResults);
    const selectedTab = useAppSelector(getSelectedAIPlaygroundTab);
    const proposedTab = useAppSelector(getProposedAIPlaygroundTab);

    const startPage = useMemo(() => !isNull(scheduleStartPage) ? scheduleStartPage.toString() : undefined, [scheduleStartPage]);
    const endPage = useMemo(() => !isNull(scheduleEndPage) ? scheduleEndPage.toString() : undefined, [scheduleEndPage]);
    const linkToScheduleStartPageDisabled = useMemo(() => !isNull(documentNumPages) && parseInt(startPage!) > documentNumPages, [startPage, documentNumPages]);
    const linkToScheduleEndPageDisabled = useMemo(() => !isNull(documentNumPages) && parseInt(endPage!) > documentNumPages, [endPage, documentNumPages]);
    const scrollToStartPageValue = useMemo(() => !isUndefined(startPage) ? parseInt(startPage) : null, [startPage]);
    const scrollToEndPageValue = useMemo(() => !isUndefined(endPage) ? parseInt(endPage) : null, [endPage]);

    const nextDisabled = useMemo(() => isUndefined(startPage) || isUndefined(endPage) || linkToScheduleStartPageDisabled || linkToScheduleEndPageDisabled, [startPage, endPage, linkToScheduleStartPageDisabled, linkToScheduleEndPageDisabled]);
    const confirmDisabled = useAppSelector(getPlaygroundQuestionsConfirmDisabled);

    const showConfirmation = useMemo(() => selectedTab === AIPlaygroundTab.CONFIRMATION && !queriesEqual && !confirmationModalVisible, [selectedTab, queriesEqual, confirmationModalVisible]);
    const aiPlaygroundTabs = Object.values(AIPlaygroundTab);
    const selectedTabIndex = aiPlaygroundTabs.indexOf(selectedTab);
    const toggleBackConfirmationModal = useCallback((value: boolean) => dispatch(toggleConfirmationModal(value)), [dispatch]);
    const setProposedPlaygroundTab = useCallback((value: AIPlaygroundTab | null) => dispatch(updateProposedTab(value)), [dispatch]);
    const selectTab = useCallback((tab: AIPlaygroundTab) => {
        if (showConfirmation) {
            toggleBackConfirmationModal(true);
            setProposedPlaygroundTab(tab);
        } else {
            dispatch(setSelectedAIPlaygroundTab(tab!));
            setProposedPlaygroundTab(null);
        }
    }, [showConfirmation, toggleBackConfirmationModal, dispatch, setProposedPlaygroundTab]);
    const previous = useCallback(() => selectTab(aiPlaygroundTabs[selectedTabIndex - 1]), [aiPlaygroundTabs, selectTab, selectedTabIndex]);
    const next = useCallback(() => selectTab(aiPlaygroundTabs[selectedTabIndex + 1]), [aiPlaygroundTabs, selectTab, selectedTabIndex]);

    const scrollToPage = useCallback((pageNumber: number | null) => dispatch(setSchedulePage(pageNumber)), [dispatch]);

    const updateSchedulePage = useCallback((key: keyof MLTestDocument, value: string) => {
        if (!isNaN(parseInt(value)) || value === '') {
            dispatch(scheduleStartAndEndPageUpdateValue(key, value === '' ? null : parseInt(value)));
            if (value === '') {
                dispatch(setSchedulePage(null));
            }
        }
    }, [dispatch]);

    const askPlaygroundQuestions = useCallback(() => dispatch(askAIPlaygroundQuestionsStarted()), [dispatch]);

    const backButton = useCallback(() => showConfirmation ? toggleBackConfirmationModal(true) : previous(), [showConfirmation, toggleBackConfirmationModal, previous]);

    const confirmWarningModal = useCallback(() => {
        dispatch(deleteNewQuestionPlaygroundQueries());
        toggleBackConfirmationModal(false);
        isNull(proposedTab) ? previous() : selectTab(proposedTab);
    }, [dispatch, toggleBackConfirmationModal, previous, selectTab, proposedTab]);

    const closeWarningModal = useCallback(() => toggleBackConfirmationModal(false), [toggleBackConfirmationModal]);

    const scrollToQuery = useCallback((queryOrder: number) => {
        const query = document.getElementById(`ai-query-${queryOrder}`);
        query?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, []);

    const addNewQuery = useCallback(() => {
        dispatch(addNewPlaygroundDocumentQuery());
        const queryOrder = selectedQueries.length;
        setTimeout(() => scrollToQuery(queryOrder), 100);
    }, [dispatch, scrollToQuery, selectedQueries]);

    const body = useMemo(() => {
        switch (selectedTab) {
            case AIPlaygroundTab.PREVIOUSLY_ASKED_QUESTIONS:
                return <AIPlaygroundPreviouslyAskedQuestions mlTestDocument={mlTestDocument} />;
            case AIPlaygroundTab.CONFIRMATION:
                return <AIPlaygroundQuery />;
            default:
                return <AIPlaygroundQuerySelect />;
        }
    }, [selectedTab, mlTestDocument]);

    const backToQuerySelect = useCallback(() => {
        dispatch(resetAIResults());
        dispatch(resetSelectedQueries());
    }, [dispatch]);
    const editAndRecalculate = useCallback(() => dispatch(resetAIResults()), [dispatch]);

    const wizardTabs = useMemo(() => aiPlaygroundTabs.map((tab, index) => ({
        label: tab,
        selected: tab === selectedTab,
        onClick: () => selectTab(tab),
        disabled: index > 0 && nextDisabled
    })), [selectTab, selectedTab, aiPlaygroundTabs, nextDisabled]);

    const showConfirmationButtons = useMemo(() => !isNull(aiResults), [aiResults]);
    const showBackButton = useMemo(() => selectedTab !== AIPlaygroundTab.ACTIVE_AI_QUESTIONS && isNull(aiResults), [selectedTab, aiResults]);
    const showNextButton = useMemo(() => selectedTab !== AIPlaygroundTab.CONFIRMATION, [selectedTab]);
    const showConfirmationTabButtons = useMemo(() => selectedTab === AIPlaygroundTab.CONFIRMATION && isNull(aiResults), [selectedTab, aiResults]);
    const confirmButtonDisabled = useMemo(() => confirmDisabled.length > 0, [confirmDisabled]);

    const buttons = useMemo(() => [
        {
            label: 'Back To Query Selection',
            onClick: backToQuerySelect,
            showButton: showConfirmationButtons,
            displayLeft: true,
            disabled: false,
            disabledTooltip: null,
            buttonWidth: '180px'
        },
        {
            label: 'Edit and Re-calculate',
            onClick: editAndRecalculate,
            showButton: showConfirmationButtons,
            disabled: false,
            disabledTooltip: null,
            buttonWidth: '180px'
        },
        {
            label: 'Back',
            onClick: backButton,
            showButton: showBackButton,
            displayLeft: true,
            disabled: false,
            disabledTooltip: null
        },
        {
            label: 'Next',
            onClick: next,
            showButton: showNextButton,
            disabled: nextDisabled,
            disabledTooltip: null
        },
        {
            label: 'New Question',
            onClick: addNewQuery,
            showButton: showConfirmationTabButtons,
            disabled: false,
            disabledTooltip: null
        },
        {
            label: 'Confirm',
            onClick: askPlaygroundQuestions,
            showButton: showConfirmationTabButtons,
            disabled: confirmButtonDisabled,
            disabledTooltip: confirmDisabled
        }
    ], [backToQuerySelect, showConfirmationButtons, editAndRecalculate, backButton, showBackButton, next, nextDisabled, showNextButton, addNewQuery, showConfirmationTabButtons, askPlaygroundQuestions, confirmButtonDisabled, confirmDisabled]);

    const title = useMemo(() => `Playground: ${mlTestDocument?.agreementType}`, [mlTestDocument]);
    const animationData = useMemo(() => getLottieAnimation(LottieAntimationTypes.ANIMATION), []);

    if (isAskingAIQuestions) {
        return <div className={styles.loadingAnimationWrapper}>
            <div className={styles.lottieAnimationWrapper}>
                <Lottie animationData={animationData} loop />
            </div>
            <div className={styles.lottieAnimationMessage}>Querying your document with the latest question set</div>
        </div>;
    }

    return (
        <div className={styles.playgroundQueriesWrapper}>
            <Wizard
                buttons={buttons}
                tabs={wizardTabs}
                title={title}
                headerIcon={LaboratoryScience}
                padding='0'
            >
                {(selectedTab === AIPlaygroundTab.ACTIVE_AI_QUESTIONS) ? <div className={styles.schedulePageInfo}>
                    <div className={styles.schedulePageWrapper}>
                        <div className={styles.schedulePageLabel}>
                            <OverflowTooltip overlayText='Start Page:' />
                        </div>
                        <div className={styles.schedulePageInput}>
                            <Text
                                testId='ai-playground-schedule-start-page'
                                value={startPage}
                                onChange={e => updateSchedulePage('scheduleStartPage', e.target.value)}
                                marginBottom='0px'
                                width='40%'
                                marginRight='10px'
                                placeholder={startPage}
                                showBoldPlaceholder
                                borderColour={startPage ? primary : amber}
                            />
                            <IconButton icon={ArrowRight} onClick={() => scrollToPage(scrollToStartPageValue)} disabled={linkToScheduleStartPageDisabled} testId='ai-playground-schedule-start-page' />
                        </div>
                    </div>
                    <div className={styles.schedulePageWrapper}>
                        <div className={styles.schedulePageLabel}>
                            <OverflowTooltip overlayText='End Page:' />
                        </div>
                        <div className={styles.schedulePageInput}>
                            <Text
                                testId='ai-playground-schedule-end-page'
                                value={endPage}
                                onChange={e => updateSchedulePage('scheduleEndPage', e.target.value)}
                                marginBottom='0px'
                                width='40%'
                                marginRight='10px'
                                placeholder=''
                                showBoldPlaceholder
                                borderColour={endPage ? primary : amber}
                            />
                            <IconButton icon={ArrowRight} onClick={() => scrollToPage(scrollToEndPageValue)} disabled={linkToScheduleEndPageDisabled} testId='ai-playground-schedule-end-page' />
                        </div>
                    </div>
                </div> : null}
                {body}
            </Wizard>
            <ConfirmationModal
                isOpen={confirmationModalVisible}
                closeModal={closeWarningModal}
                confirm={confirmWarningModal}>
                <div className={styles.backModalWrapper}>
                    <ModalHeader
                        testId='warning-modal'
                        label='Warning'
                        icon={WarningSign}
                    />
                    <div data-testid='warning-content' className={styles.backModalContent}>Are you sure you want to go back? Any new questions added will be removed.</div>
                </div>
            </ConfirmationModal>
        </div>
    );
};
